import type {
  SupplierMatchStatus,
  SupplierResponseIssue,
  SupplierResponseLineValidation,
  SupplierResponseValidation,
} from './types'

export const SUPPLIER_RESPONSE_CURRENCY_TOLERANCE_EUR = 0.02

export type SupplierResponseValidationItemInput = {
  id: string
  pricingItemId: string | null
  pricingItemFound: boolean
  requestedQuantity: number | null
  supplierPosition: string | null
  supplierQuantity: number | null
  supplierUnitCostEur: number | null
  supplierLineCostEur: number | null
  storedMatchStatus?: SupplierMatchStatus | null
  storedReviewRequired?: boolean
  resolutionResolved?: boolean
  resolutionAccepted?: boolean
  assessmentExcluded?: boolean
  customerExcluded?: boolean
  includedInSupplierTotal?: boolean | null
  allocatedCostEur?: number | null
  allocationsAccepted?: boolean
  allocationIssues?: string[]
}

export type SupplierResponseValidationInput = {
  supplierTotalNet: number | null
  originalQuotePresent: boolean
  processingWarnings?: string[]
  items: SupplierResponseValidationItemInput[]
}

export function roundSupplierCurrency(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export function supplierCurrencyMatches(left: number, right: number) {
  return Math.abs(roundSupplierCurrency(left) - roundSupplierCurrency(right)) <= SUPPLIER_RESPONSE_CURRENCY_TOLERANCE_EUR + Number.EPSILON
}

function issue(code: string, message: string, severity: 'error' | 'warning', supplierQuoteItemId?: string): SupplierResponseIssue {
  return { code, message, severity, ...(supplierQuoteItemId ? { supplierQuoteItemId } : {}) }
}

function finiteNonNegative(value: number | null) {
  return value !== null && Number.isFinite(value) && value >= 0
}

function finitePositive(value: number | null) {
  return value !== null && Number.isFinite(value) && value > 0
}

/**
 * Opening/Pricing Item quantity is an item count, while a supplier may quote
 * by area (m²), length, etc.  Decimal supplier quantities therefore represent
 * a different supplier unit and must not be rejected as an item-count
 * mismatch. Whole-number supplier quantities continue to be compared with
 * the requested count.
 */
export function supplierQuantityMatchesRequested(requested: number | null, supplier: number | null) {
  if (requested === null || supplier === null) return true
  if (!Number.isFinite(requested) || !Number.isFinite(supplier) || supplier <= 0) return false
  if (!Number.isInteger(supplier)) return true
  return requested === supplier
}

export function validateSupplierResponseLine(item: SupplierResponseValidationItemInput): SupplierResponseLineValidation {
  const issues: SupplierResponseIssue[] = []
  const linked = Boolean(item.pricingItemId)
  const allocationOnly = linked && item.supplierUnitCostEur === null && item.supplierLineCostEur === null
    && finiteNonNegative(item.allocatedCostEur ?? null)
  for (const message of item.allocationIssues ?? []) issues.push(issue('SUPPLIER_RESPONSE_ALLOCATION_INVALID', message, 'error', item.id))
  const resolutionResolved = !linked && item.resolutionResolved === true
  if (!linked && !resolutionResolved) {
    issues.push(issue('SUPPLIER_RESPONSE_LINE_UNMATCHED', 'This supplier line is not linked to a requested Pricing Item.', 'warning', item.id))
  } else if (linked && !item.pricingItemFound) {
    issues.push(issue('SUPPLIER_RESPONSE_PRICING_ITEM_MISSING', 'The linked Pricing Item snapshot could not be loaded.', 'error', item.id))
  }

  if (linked && !allocationOnly && !finitePositive(item.supplierQuantity)) {
    issues.push(issue('SUPPLIER_RESPONSE_QUANTITY_MISSING', 'Enter a positive supplier quantity.', 'error', item.id))
  }
  if (item.supplierQuantity !== null && !finitePositive(item.supplierQuantity)) {
    if (!issues.some((entry) => entry.code === 'SUPPLIER_RESPONSE_QUANTITY_MISSING')) {
      issues.push(issue('SUPPLIER_RESPONSE_QUANTITY_INVALID', 'Supplier quantity must be a positive number.', 'error', item.id))
    }
  }
  if (item.supplierUnitCostEur !== null && !finiteNonNegative(item.supplierUnitCostEur)) {
    issues.push(issue('SUPPLIER_RESPONSE_UNIT_COST_INVALID', 'Supplier unit cost cannot be negative.', 'error', item.id))
  }
  if (item.supplierLineCostEur !== null && !finiteNonNegative(item.supplierLineCostEur)) {
    issues.push(issue('SUPPLIER_RESPONSE_LINE_COST_INVALID', 'Supplier line total cannot be negative.', 'error', item.id))
  }
  // `not_quoted` is the parser's initial state when it could not associate a
  // primary supplier line.  It must not remain sticky after a reviewer has
  // entered a complete response: populated costs and quantity are enough for
  // the normal validation below to determine whether the line is matched,
  // partial, or pending.  Keep the status only while the line is still empty.
  const notQuoted = item.storedMatchStatus === 'not_quoted'
    && item.supplierUnitCostEur === null
    && item.supplierLineCostEur === null
    && !allocationOnly
  if (!notQuoted && !allocationOnly && item.supplierUnitCostEur === null && item.supplierLineCostEur === null) {
    issues.push(issue('SUPPLIER_RESPONSE_COST_MISSING', 'Enter a supplier unit cost or stated line total.', 'error', item.id))
  }
  if (!allocationOnly && !item.supplierPosition?.trim()) {
    issues.push(issue('SUPPLIER_RESPONSE_POSITION_MISSING', 'Supplier position is blank.', 'warning', item.id))
  }
  if (
    linked && !allocationOnly && item.pricingItemFound && item.requestedQuantity !== null && item.supplierQuantity !== null
    && !supplierQuantityMatchesRequested(item.requestedQuantity, item.supplierQuantity)
  ) {
    issues.push(issue(
      'SUPPLIER_RESPONSE_QUANTITY_MISMATCH',
      `Requested quantity ${item.requestedQuantity} differs from supplier quantity ${item.supplierQuantity}.`,
      'warning',
      item.id,
    ))
  }

  const calculatedLineCostEur = item.supplierQuantity !== null && finiteNonNegative(item.supplierUnitCostEur)
    ? roundSupplierCurrency(item.supplierQuantity * item.supplierUnitCostEur!)
    : null
  if (
    calculatedLineCostEur !== null && finiteNonNegative(item.supplierLineCostEur)
    && !supplierCurrencyMatches(calculatedLineCostEur, item.supplierLineCostEur!)
  ) {
    issues.push(issue(
      'SUPPLIER_RESPONSE_ARITHMETIC_MISMATCH',
      `Quantity × unit cost is €${calculatedLineCostEur.toFixed(2)}, but the supplier line total is €${item.supplierLineCostEur!.toFixed(2)}.`,
      'warning',
      item.id,
    ))
  }
  const effectiveLineCostEur = finiteNonNegative(item.supplierLineCostEur)
    ? roundSupplierCurrency(item.supplierLineCostEur!)
    : calculatedLineCostEur
  if (!linked && effectiveLineCostEur === null) {
    issues.push(issue('SUPPLIER_RESPONSE_UNMATCHED_TOTAL_MISSING', 'Enter a stated total or enough information to calculate this unmatched line.', 'error', item.id))
  }

  const fullyIgnored = item.assessmentExcluded === true && item.customerExcluded === true && item.includedInSupplierTotal === false
  const materialIssues = issues.filter((entry) => entry.code !== 'SUPPLIER_RESPONSE_POSITION_MISSING' && !fullyIgnored)
  if (fullyIgnored) issues.splice(0, issues.length)
  let supplierMatchStatus: SupplierMatchStatus = 'matched'
  if (!linked) supplierMatchStatus = resolutionResolved ? (item.resolutionAccepted ? 'matched' : 'review') : 'unmatched'
  else if (notQuoted) supplierMatchStatus = 'not_quoted'
  else if (materialIssues.some((entry) => entry.severity === 'error')) supplierMatchStatus = 'pending'
  else if (allocationOnly) supplierMatchStatus = item.allocationsAccepted ? 'matched' : 'review'
  else if (materialIssues.length) supplierMatchStatus = 'partial'

  if (linked && !allocationOnly && item.storedMatchStatus === 'review' && supplierMatchStatus !== 'pending') {
    supplierMatchStatus = 'review'
    if (!issues.some((entry) => entry.code === 'SUPPLIER_RESPONSE_TECHNICAL_REVIEW')) issues.push(issue('SUPPLIER_RESPONSE_TECHNICAL_REVIEW', 'This matched line has a supplier technical difference awaiting confirmation.', 'warning', item.id))
  }
  return {
    supplierMatchStatus,
    reviewRequired: allocationOnly ? supplierMatchStatus !== 'matched' : resolutionResolved
      ? item.resolutionAccepted !== true
      : item.resolutionAccepted ? supplierMatchStatus === 'not_quoted' || supplierMatchStatus === 'review' : supplierMatchStatus !== 'matched',
    calculatedLineCostEur,
    effectiveLineCostEur,
    lineTotalSource: finiteNonNegative(item.supplierLineCostEur) ? 'supplier' : calculatedLineCostEur !== null ? 'calculated' : null,
    issues,
  }
}

export function validateSupplierQuoteResponse(input: SupplierResponseValidationInput): {
  validation: SupplierResponseValidation
  lineValidations: Map<string, SupplierResponseLineValidation>
} {
  const lineValidations = new Map(input.items.map((item) => [item.id, validateSupplierResponseLine(item)]))
  const issues = input.items.flatMap((item) => lineValidations.get(item.id)?.issues ?? [])
  input.processingWarnings?.map((warning) => warning.trim()).filter(Boolean).forEach((warning, index) => {
    issues.push(issue(`SUPPLIER_RESPONSE_PROCESSING_WARNING_${index + 1}`, warning, 'warning'))
  })
  if (!input.originalQuotePresent) issues.push(issue('SUPPLIER_RESPONSE_ORIGINAL_QUOTE_MISSING', 'Attach the original supplier quote before review.', 'error'))
  if (!finiteNonNegative(input.supplierTotalNet)) issues.push(issue('SUPPLIER_RESPONSE_TOTAL_MISSING', 'Enter the supplier quoted net total.', 'error'))

  const expectedItemCount = input.items.filter((item) => Boolean(item.pricingItemId)).length
  if (!expectedItemCount) issues.push(issue('SUPPLIER_RESPONSE_EXPECTED_ITEMS_MISSING', 'No requested Pricing Items are available for this supplier response.', 'error'))
  const effectiveTotals = input.items.map((item) => lineValidations.get(item.id)?.effectiveLineCostEur ?? null)
  const statedLineTotalEur = roundSupplierCurrency(input.items.reduce((sum, item) => sum + (finiteNonNegative(item.supplierLineCostEur) ? item.supplierLineCostEur! : 0), 0))
  const calculatedLineTotalEur = roundSupplierCurrency(effectiveTotals.reduce<number>((sum, value) => sum + (value ?? 0), 0))
  const assessedItems = input.items.filter((item) => item.assessmentExcluded !== true)
    // RFQ target rows with no commercial price are not printed supplier rows.
    .filter((item) => !item.pricingItemId || item.supplierUnitCostEur !== null || item.supplierLineCostEur !== null)
  const assessedLineTotalEur = roundSupplierCurrency(assessedItems.reduce((sum, item) => sum + (finiteNonNegative(item.supplierLineCostEur) ? item.supplierLineCostEur! : 0), 0))
  const assessmentExcludedAmountEur = roundSupplierCurrency(input.items.filter((item) => item.assessmentExcluded === true).reduce((sum, item) => sum + (lineValidations.get(item.id)?.effectiveLineCostEur ?? 0), 0))
  const assessmentAdjustmentEur = roundSupplierCurrency(input.items.filter((item) => item.assessmentExcluded === true && item.includedInSupplierTotal === true).reduce((sum, item) => sum + (lineValidations.get(item.id)?.effectiveLineCostEur ?? 0), 0))
  const assessedSupplierTotalNet = finiteNonNegative(input.supplierTotalNet)
    ? roundSupplierCurrency(input.supplierTotalNet! - assessmentAdjustmentEur)
    : null
  const allLinesHaveStatedTotals = assessedItems.every((item) => finiteNonNegative(item.supplierLineCostEur))
  const reconciliationDeltaEur = assessedSupplierTotalNet !== null
    ? roundSupplierCurrency(assessedLineTotalEur - assessedSupplierTotalNet)
    : null
  const reconciled = allLinesHaveStatedTotals && reconciliationDeltaEur !== null && supplierCurrencyMatches(assessedLineTotalEur, assessedSupplierTotalNet!)
  if (reconciliationDeltaEur !== null && !reconciled) {
    issues.push(issue(
      'SUPPLIER_RESPONSE_TOTAL_MISMATCH',
      allLinesHaveStatedTotals
        ? `Assessed supplier line totals differ from the assessed quoted net total by €${Math.abs(reconciliationDeltaEur).toFixed(2)}.`
        : 'One or more supplier-stated line totals are blank, so the quoted net total cannot be fully reconciled.',
      'warning',
    ))
  }

  const lineValues = [...lineValidations.values()]
  const validation: SupplierResponseValidation = {
    expectedItemCount,
    matchedItemCount: input.items.filter((item) => Boolean(item.pricingItemId) && lineValidations.get(item.id)?.supplierMatchStatus === 'matched').length,
    responseItemCount: input.items.length,
    unmatchedItemCount: lineValues.filter((value) => value.supplierMatchStatus === 'unmatched').length,
    reviewItemCount: lineValues.filter((value) => value.reviewRequired).length,
    missingQuantityCount: issues.filter((entry) => entry.code === 'SUPPLIER_RESPONSE_QUANTITY_MISSING').length,
    missingCostCount: issues.filter((entry) => entry.code === 'SUPPLIER_RESPONSE_COST_MISSING').length,
    arithmeticMismatchCount: issues.filter((entry) => entry.code === 'SUPPLIER_RESPONSE_ARITHMETIC_MISMATCH').length,
    quantityMismatchCount: issues.filter((entry) => entry.code === 'SUPPLIER_RESPONSE_QUANTITY_MISMATCH').length,
    provisionalLineCount: lineValues.filter((value) => value.lineTotalSource === 'calculated').length,
    supplierTotalNet: finiteNonNegative(input.supplierTotalNet) ? roundSupplierCurrency(input.supplierTotalNet!) : null,
    statedLineTotalEur,
    calculatedLineTotalEur,
    assessmentExcludedCount: input.items.filter((item) => item.assessmentExcluded === true).length,
    assessmentExcludedAmountEur,
    assessedLineTotalEur,
    assessedSupplierTotalNet,
    assessmentAdjustmentEur,
    reconciliationDeltaEur,
    reconciled,
    originalQuotePresent: input.originalQuotePresent,
    readyForReview: !issues.some((entry) => entry.severity === 'error'),
    requiresWarningConfirmation: issues.some((entry) => entry.severity === 'warning'),
    issues,
  }
  return { validation, lineValidations }
}
